import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  Link,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Security, Smartphone, VpnKey, Fingerprint } from '@mui/icons-material';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { complete2FALogin } from '../../TwoFactorActions';
import { authenticatePasskey } from '../../PasskeyActions';
import { receiveLogin } from '../../AuthActions';

const messages = defineMessages({
  title: {
    id: 'TwoFactorLogin.Title',
    defaultMessage: 'Two-Factor Authentication',
  },
  description: {
    id: 'TwoFactorLogin.Description',
    defaultMessage:
      'Enter the 6-digit code from your authenticator app or use one of your backup codes.',
  },
  verificationCode: {
    id: 'TwoFactorLogin.VerificationCode',
    defaultMessage: 'Verification code',
  },
  verify: {
    id: 'TwoFactorLogin.Verify',
    defaultMessage: 'Verify',
  },
  usingBackupCode: {
    id: 'TwoFactorLogin.UsingBackupCode',
    defaultMessage: 'Using a backup code?',
  },
  usingAuthenticator: {
    id: 'TwoFactorLogin.UsingAuthenticator',
    defaultMessage: 'Using authenticator app?',
  },
  backupCodeLabel: {
    id: 'TwoFactorLogin.BackupCodeLabel',
    defaultMessage: 'Backup code',
  },
  backupCodeDescription: {
    id: 'TwoFactorLogin.BackupCodeDescription',
    defaultMessage: 'Enter one of your 8-character backup codes.',
  },
  invalidCode: {
    id: 'TwoFactorLogin.InvalidCode',
    defaultMessage: 'Invalid verification code. Please try again.',
  },
  backupCodeUsed: {
    id: 'TwoFactorLogin.BackupCodeUsed',
    defaultMessage: 'Backup code used successfully. You have {remaining} backup codes remaining.',
  },
  problemsAccessing: {
    id: 'TwoFactorLogin.ProblemsAccessing',
    defaultMessage: 'Having problems accessing your account?',
  },
  contactSupport: {
    id: 'TwoFactorLogin.ContactSupport',
    defaultMessage: 'Contact support',
  },
  passkeyTitle: {
    id: 'TwoFactorLogin.PasskeyTitle',
    defaultMessage: 'Sign in with your passkey',
  },
  passkeyDescription: {
    id: 'TwoFactorLogin.PasskeyDescription',
    defaultMessage: 'Use your biometric authentication or security key to sign in securely.',
  },
  passkeyButton: {
    id: 'TwoFactorLogin.PasskeyButton',
    defaultMessage: 'Use Passkey',
  },
  passkeyFallback: {
    id: 'TwoFactorLogin.PasskeyFallback',
    defaultMessage: 'Or use authenticator app',
  },
  passkeyError: {
    id: 'TwoFactorLogin.PasskeyError',
    defaultMessage: 'Passkey authentication failed. Please try again or use another method.',
  },
  or: {
    id: 'TwoFactorLogin.Or',
    defaultMessage: 'o',
  },
});

function TwoFactorLogin({ userId, hasPasskeys, _provider, onComplete, onError }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const [code, setCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);
  const [error, setError] = useState('');
  const [passkeyError, setPasskeyError] = useState('');
  const [backupCodeUsed, setBackupCodeUsed] = useState(null);
  const [showCodeForm, setShowCodeForm] = useState(!hasPasskeys); // Show code form immediately if no passkeys

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();
      if (!code.trim()) return;

      setIsLoading(true);
      setError('');
      setBackupCodeUsed(null);

      try {
        const response = await dispatch(complete2FALogin(userId, code.trim()));

        if (response.user) {
          // Dispatch login success
          dispatch(receiveLogin(response.user));

          if (response.usedBackupCode) {
            setBackupCodeUsed(response.remainingBackupCodes);
          }

          if (onComplete) {
            onComplete(response);
          }
        }
      } catch (err) {
        const errorMessage = err.message || formatMessage(messages.invalidCode);
        setError(errorMessage);
        if (onError) {
          onError(err);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [code, userId, dispatch, onComplete, onError, formatMessage],
  );

  const handleCodeChange = useCallback(
    e => {
      const { value } = e.target;
      // For TOTP codes, limit to 6 digits
      // For backup codes, allow 8 characters
      const maxLength = isBackupCode ? 8 : 6;

      if (value.length <= maxLength) {
        setCode(value);
        setError('');
      }
    },
    [isBackupCode],
  );

  const toggleCodeType = useCallback(() => {
    setIsBackupCode(!isBackupCode);
    setCode('');
    setError('');
    setBackupCodeUsed(null);
  }, [isBackupCode]);

  const handlePasskeyAuth = useCallback(async () => {
    setIsPasskeyLoading(true);
    setPasskeyError('');
    setError('');

    try {
      // For 2FA flow, pass the userId to identify the user during passkey authentication
      const response = await dispatch(authenticatePasskey(null, userId));

      if (response.user) {
        // Dispatch login success
        dispatch(receiveLogin(response.user));

        if (onComplete) {
          onComplete(response);
        }
      }
    } catch (err) {
      const errorMessage = err.message || formatMessage(messages.passkeyError);
      setPasskeyError(errorMessage);
      if (onError) {
        onError(err);
      }
    } finally {
      setIsPasskeyLoading(false);
    }
  }, [dispatch, userId, onComplete, formatMessage, onError]);

  const handleShowCodeForm = useCallback(() => {
    setShowCodeForm(true);
    setPasskeyError('');
  }, []);

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            {hasPasskeys && !showCodeForm ? (
              <>
                <Fingerprint sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="h1" gutterBottom>
                  {formatMessage(messages.passkeyTitle)}
                </Typography>
              </>
            ) : (
              <>
                <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="h1" gutterBottom>
                  {formatMessage(messages.title)}
                </Typography>
              </>
            )}
          </Box>

          {/* Passkey Section - Primary when available */}
          {hasPasskeys && !showCodeForm && (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage(messages.passkeyDescription)}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handlePasskeyAuth}
                disabled={isPasskeyLoading}
                startIcon={<Fingerprint />}
                sx={{ mb: 2 }}
              >
                {isPasskeyLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  formatMessage(messages.passkeyButton)
                )}
              </Button>

              {passkeyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {passkeyError}
                </Alert>
              )}

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatMessage(messages.or)}
                </Typography>
              </Divider>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleShowCodeForm}
                  sx={{ textDecoration: 'none' }}
                >
                  {formatMessage(messages.passkeyFallback)}
                </Link>
              </Box>
            </>
          )}

          {/* Code Form Section - Primary when no passkeys or fallback */}
          {(!hasPasskeys || showCodeForm) && (
            <>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {isBackupCode
                    ? formatMessage(messages.backupCodeDescription)
                    : formatMessage(messages.description)}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={formatMessage(
                    isBackupCode ? messages.backupCodeLabel : messages.verificationCode,
                  )}
                  value={code}
                  onChange={handleCodeChange}
                  placeholder={isBackupCode ? '12345678' : '123456'}
                  inputProps={{
                    maxLength: isBackupCode ? 8 : 6,
                    style: {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.2em',
                      fontFamily: 'monospace',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {isBackupCode ? <VpnKey /> : <Smartphone />}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                  error={!!error}
                  helperText={error}
                  autoFocus={!hasPasskeys || showCodeForm}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!code.trim() || isLoading}
                  sx={{ mb: 2 }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    formatMessage(messages.verify)
                  )}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={toggleCodeType}
                    sx={{ textDecoration: 'none' }}
                  >
                    {formatMessage(
                      isBackupCode ? messages.usingAuthenticator : messages.usingBackupCode,
                    )}
                  </Link>
                </Box>
              </Box>

              {backupCodeUsed !== null && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {formatMessage(messages.backupCodeUsed, { remaining: backupCodeUsed })}
                </Alert>
              )}
            </>
          )}

          <Box
            sx={{
              textAlign: 'center',
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              {formatMessage(messages.problemsAccessing)}
            </Typography>
            <Link
              href="mailto:support@promoter.ninja"
              variant="caption"
              sx={{ textDecoration: 'none' }}
            >
              {formatMessage(messages.contactSupport)}
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default TwoFactorLogin;
